import { useCallback, useEffect } from 'react'
import type { BillingPlanV2Tier, BillingPlanV2TrialTier } from '@highlight/common/types'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { refreshBilling } from '@renderer/chat/store/global-billing'
import { useAddCredits } from '@renderer/chat/hooks/useAddCredits'
import { useAddMeetingMinutes } from '@renderer/chat/hooks/use-add-meeting-minutes'
import { useChangeV2Plan } from '@renderer/chat/hooks/use-change-v2-plan'
import { useCheckout } from '@renderer/chat/hooks/useCheckout'
import type { TopUpKindSpec, TopUpSelection } from './top-up-kinds'
import { useBillingRefreshGate } from './use-billing-refresh-gate'
import { usePurchaseLatch } from './use-purchase-latch'

/**
 * Identifies the surface a paywall was shown on, for the Paywall analytics
 * events ('sidepanel home', 'meeting view', 'subscription settings').
 */
interface PaywallSurface {
  tier: BillingPlanV2Tier
  from: string
  /** Fires once a purchase commits, for surfaces that dismiss themselves instead of unmounting. */
  onPurchased?: () => void
}

/** Records that a paywall surfaced, once per tier the surface resolves. */
export function usePaywallShown({ tier, from }: PaywallSurface, feature: string, reason: string) {
  useEffect(() => {
    AmplitudeRenderer.trackEvent('Paywall', { action: 'shown', feature, reason, plan: tier, from })
  }, [tier, from, feature, reason])
}

/**
 * The upgrade half of a paywall, shared by the credits and meeting-hours top-ups.
 * The latch comes from the caller so a top-up and an upgrade can't be started
 * together — either request in flight disables both CTAs.
 */
function useTierUpgrade({ tier, from, onPurchased }: PaywallSurface, latch: ReturnType<typeof usePurchaseLatch>) {
  const { changePlan, isPending } = useChangeV2Plan()
  const { needsBillingRefresh, commit, clear } = useBillingRefreshGate()
  const { begin, settle, fail } = latch

  const onUpgrade = useCallback(
    (target: BillingPlanV2Tier) => {
      AmplitudeRenderer.trackEvent('Paywall', { action: 'upgrade clicked', plan: tier, target, from })
      begin()
      // Card on file bills immediately via Orb; otherwise this opens Stripe
      // checkout. On success the billing refresh raises the allowance and the
      // paywall dismisses itself.
      changePlan(target, {
        onPlanChanged: (billingRefreshed) => {
          settle()
          commit(billingRefreshed, onPurchased)
        },
        onCheckoutOpened: () => {},
        onError: (_step, error) => fail(error),
      })
    },
    [tier, from, onPurchased, changePlan, begin, settle, fail, commit],
  )

  return { onUpgrade, isUpgradePending: isPending, needsBillingRefresh, clearBillingRefresh: clear }
}

/**
 * Wiring for either top-up: buy the units, or move up a tier. The spec decides
 * which mutation runs and how the purchase is reported; everything else --
 * latch, billing refresh, upgrade path -- is the same for both.
 */
export function useTopUp(spec: TopUpKindSpec, { tier, from, onPurchased }: PaywallSurface) {
  // Both mutations mount unconditionally: they are inert until called, and
  // picking one behind a hook call would break the rules of hooks the moment a
  // surface swapped kinds. This is the one place the two kinds diverge.
  const addCredits = useAddCredits()
  const addMinutes = useAddMeetingMinutes()
  const isPending = spec.kind === 'credits' ? addCredits.isPending : addMinutes.isPending
  const latch = usePurchaseLatch()
  const { begin, settle, fail } = latch
  const { onUpgrade, isUpgradePending, needsBillingRefresh, clearBillingRefresh } = useTierUpgrade(
    { tier, from, onPurchased },
    latch,
  )

  const onPurchase = useCallback(
    (selection: TopUpSelection) => {
      AmplitudeRenderer.trackEvent('Paywall', {
        action: spec.analytics.action,
        plan: tier,
        dollars: selection.dollars,
        [spec.analytics.unit]: selection.units,
        from,
      })
      begin()
      const callbacks = {
        onSuccess: () => {
          settle()
          refreshBilling()
          onPurchased?.()
        },
        onError: fail,
      }
      if (spec.kind === 'credits') {
        addCredits.mutate(String(selection.dollars), callbacks)
      } else {
        addMinutes.mutate(selection.units, callbacks)
      }
    },
    [spec, tier, from, onPurchased, addCredits, addMinutes, begin, settle, fail],
  )

  return {
    onPurchase,
    onUpgrade,
    isPending: isPending || latch.settled,
    isUpgradePending: isUpgradePending || latch.settled,
    errorMessage: latch.errorMessage,
    needsBillingRefresh,
    clearBillingRefresh,
  }
}

/** Wiring for the plan-ended paywall: re-subscribe to the tier that lapsed. */
export function usePlanRenewal({ tier, from, onPurchased }: PaywallSurface) {
  const { changePlan, isPending } = useChangeV2Plan()
  const { needsBillingRefresh, commit, clear } = useBillingRefreshGate()
  const { errorMessage, settled, begin, settle, fail } = usePurchaseLatch()

  const onRenew = useCallback(() => {
    AmplitudeRenderer.trackEvent('Paywall', { action: 'renew clicked', plan: tier, from })
    begin()
    changePlan(tier, {
      onPlanChanged: (billingRefreshed) => {
        settle()
        commit(billingRefreshed, onPurchased)
      },
      onCheckoutOpened: () => {},
      onError: (_step, error) => fail(error),
    })
  }, [tier, from, onPurchased, changePlan, begin, settle, fail, commit])

  return { onRenew, isPending: isPending || settled, errorMessage, needsBillingRefresh, clearBillingRefresh: clear }
}

/**
 * Wiring for the plan promo: buy the tier outright. Unlike the top-ups and the
 * renewal this sells to a plan that holds no tier, so it reports whether a card
 * exists. Without one `changePlan` opens Stripe checkout, and the CTA must not
 * promise an immediate charge.
 */
export function usePlanUpgrade({ tier, from, onPurchased }: PaywallSurface) {
  const { changePlan, hasPaymentMethod, isPending } = useChangeV2Plan()
  const { needsBillingRefresh, commit, clear } = useBillingRefreshGate()
  const { errorMessage, settled, begin, settle, fail } = usePurchaseLatch()

  const onUpgrade = useCallback(() => {
    AmplitudeRenderer.trackEvent('Paywall', { action: 'upgrade clicked', plan: tier, target: tier, from })
    begin()
    changePlan(tier, {
      onPlanChanged: (billingRefreshed) => {
        settle()
        commit(billingRefreshed, onPurchased)
      },
      // No latch: checkout runs in the browser and can be abandoned, and the
      // latch only releases on unmount. createCheckout's own isPending drops
      // when the tab opens, which returns the CTA to normal. useCheckout has
      // already told the main process to reconcile until the purchase lands.
      onCheckoutOpened: () => {},
      onError: (_step, error) => fail(error),
    })
  }, [tier, from, onPurchased, changePlan, begin, settle, fail, commit])

  return {
    onUpgrade,
    hasPaymentMethod,
    isPending: isPending || settled,
    errorMessage,
    needsBillingRefresh,
    clearBillingRefresh: clear,
  }
}

/**
 * Wiring for the trial-ending paywall: save a card so the trial converts to its
 * tier instead of ending on free. Always the tier's trial checkout, never the
 * in-app plan change: nothing is due, so the server would re-select the trial
 * without asking for a card. For a user already on that trial the checkout only
 * collects the card and keeps the end date (fulfillV2TrialCheckout).
 */
export function useTrialKeep({ tier, from }: PaywallSurface & { tier: BillingPlanV2TrialTier }) {
  const { mutate: createCheckout, isPending } = useCheckout()
  const { errorMessage, begin, fail } = usePurchaseLatch()

  const onKeep = useCallback(() => {
    AmplitudeRenderer.trackEvent('Paywall', { action: 'keep trial clicked', plan: tier, from })
    begin()
    // No latch across the browser checkout, for the reason usePlanUpgrade gives.
    createCheckout({ plan: tier, trial: true }, { onError: (error) => fail(error) })
  }, [tier, from, createCheckout, begin, fail])

  return { onKeep, isPending, errorMessage }
}
